import type { ComponentInternalInstance, ComponentPublicInstance, VNode } from 'vue'

/**
 * The child component instances of a mounted component — Vue 3's replacement for `$children`.
 *
 * `$children` is gone. Vue 2 maintained that array on every instance; Vue 3 keeps only the rendered
 * vnode tree, so the equivalent is to walk `subTree` and pick out the vnodes that are components.
 * Everything in the bundle that walked the form for validation or reset depended on it: BsForm's
 * `validateComponentTree`/`resetComponentTree`, BsTabStepper's required-field guard, and
 * `useBsComponent`'s `isFormValid`.
 *
 * DIRECT children only, matching `$children`: a component vnode's own subtree is not descended into,
 * because its children belong to it. The callers recurse themselves, as they did before.
 *
 * Fragments and Suspense hold their children in an array `children`, so those are walked through
 * without counting as a level.
 */
export function childComponents (instance: ComponentPublicInstance | null | undefined): ComponentPublicInstance[] {
  const internal = (instance as any)?.$ as ComponentInternalInstance | undefined

  if (internal?.subTree === void 0) {
    return []
  }

  const found: ComponentPublicInstance[] = []

  const walk = (vnode: VNode | null | undefined): void => {
    if (vnode === null || vnode === void 0 || typeof vnode !== 'object') {
      return
    }

    if (vnode.component?.proxy) {
      found.push(vnode.component.proxy)
      return
    }

    if (Array.isArray(vnode.children)) {
      for (const child of vnode.children as VNode[]) {
        walk(child)
      }
    }
  }

  walk(internal.subTree)

  return found
}
